import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Search, Filter, Target, Swords, Users, UserPlus, X, ChevronDown, ArrowLeft } from 'lucide-react';
import HamburgerMenu from '../components/HamburgerMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FindPeople = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [suggestedOpponents, setSuggestedOpponents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    skill_level: '',
    fitness_goal: '',
    location: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) {
        navigate('/');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const [usersRes, suggestedRes] = await Promise.allSettled([
        axios.get(`${API}/users/search`, { params: { limit: 50 } }),
        axios.get(`${API}/users/suggested-opponents`)
      ]);

      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data.users || []);
      }
      if (suggestedRes.status === 'fulfilled') {
        setSuggestedOpponents(suggestedRes.value.data.suggested_opponents || []);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading warriors:', error);
      toast.error('Failed to load warriors');
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    try {
      const params = {
        query: searchQuery,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await axios.get(`${API}/users/search`, { params });
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const sendFriendRequest = async (userId, username) => {
    try {
      await axios.post(`${API}/friends/request`, null, {
        params: { friend_identifier: username }
      });
      toast.success(`Friend request sent to ${username}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send friend request');
    }
  };

  const challengeUser = async (username) => {
    // Navigate to battle arena with the username pre-filled
    navigate('/battle-arena', { state: { challengeUsername: username } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-blood-950">
        <HamburgerMenu />
        <div className="flex items-center justify-center pt-20">
          <div className="text-blood-400 text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-blood-950 text-white pb-20">
      <HamburgerMenu />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Back Button */}
        <button onClick={() => navigate('/hub')} className="flex items-center gap-2 text-stone-400 hover:text-white mb-4 transition-colors" data-testid="back-to-hub-btn">
          <ArrowLeft className="w-4 h-4" />
          Back to Hub
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-blood-400 mb-2 tracking-wider flex items-center justify-center gap-2">
            <Search className="w-8 h-8" />
            FIND PEOPLE
          </h1>
          <p className="text-stone-400 text-sm">Discover • Connect • Train Together</p>
        </div>

        {/* Search Bar */}
        <Card className="glass border-blood-700 p-4 mb-4">
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
              className="flex-1 glass border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-blood-700 text-sm"
              placeholder="Search by username or email..."
            />
            <Button
              onClick={searchUsers}
              className="bg-blood-700 hover:bg-blood-800 px-4"
            >
              <Search className="w-5 h-5" />
            </Button>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-stone-400 text-sm hover:text-blood-400 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 mt-3 border-t border-stone-800">
              <div>
                <label className="text-stone-400 text-xs mb-1 block">Skill Level</label>
                <select
                  value={filters.skill_level}
                  onChange={(e) => setFilters({...filters, skill_level: e.target.value})}
                  className="w-full glass border border-stone-700 text-white px-3 py-2 rounded text-sm"
                >
                  <option value="">All Levels</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div>
                <label className="text-stone-400 text-xs mb-1 block">Fitness Goal</label>
                <select
                  value={filters.fitness_goal}
                  onChange={(e) => setFilters({...filters, fitness_goal: e.target.value})}
                  className="w-full glass border border-stone-700 text-white px-3 py-2 rounded text-sm"
                >
                  <option value="">All Goals</option>
                  <option value="bulk">Bulk</option>
                  <option value="tone">Tone</option>
                  <option value="lose_weight">Lose Weight</option>
                  <option value="maintain">Maintain</option>
                </select>
              </div>

              <div>
                <label className="text-stone-400 text-xs mb-1 block">Location</label>
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => setFilters({...filters, location: e.target.value})}
                  className="w-full glass border border-stone-700 text-white px-3 py-2 rounded text-sm"
                  placeholder="City or State"
                />
              </div>
            </div>
          )}
        </Card>

        {/* Suggested Opponents */}
        {suggestedOpponents.length > 0 && (
          <Card className="glass border-amber-700 p-4 mb-4">
            <h2 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5" />
              SUGGESTED FOR YOU
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {suggestedOpponents.map((opponent) => (
                <div
                  key={opponent.id}
                  className="glass border border-amber-700 rounded-lg p-3 text-center hover:border-amber-500 transition-all"
                >
                  <div className="text-sm font-bold text-amber-400 mb-1 truncate">
                    {opponent.username || opponent.email?.split('@')[0]}
                  </div>
                  <div className="text-xs text-stone-400 mb-2">
                    {opponent.skill_level}
                  </div>
                  <Button
                    onClick={() => challengeUser(opponent.username || opponent.email)}
                    className="w-full bg-blood-700 hover:bg-blood-800 text-xs py-1"
                    size="sm"
                  >
                    <Swords className="w-3 h-3 mr-1" />
                    Battle
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* All Users */}
        <Card className="glass border-blood-700 p-4">
          <h2 className="text-lg font-bold text-blood-400 mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" />
            BROWSE PEOPLE ({users.length})
          </h2>
          
          {users.length === 0 ? (
            <div className="text-center py-10 text-stone-400">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No one found. Try adjusting your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="glass border border-stone-700 rounded-lg p-3 hover:border-blood-700 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-blood-400 truncate">
                        {user.username || user.email?.split('@')[0]}
                      </div>
                      <div className="text-xs text-stone-400">
                        {user.skill_level} • {user.fitness_goal?.replace('_', ' ')}
                      </div>
                      {user.location && (
                        <div className="text-xs text-stone-500 truncate">{user.location}</div>
                      )}
                    </div>
                  </div>

                  {user.stats && (
                    <div className="grid grid-cols-3 gap-1 mb-2 text-center">
                      <div className="glass border border-stone-800 rounded p-1">
                        <div className="text-sm font-bold text-blood-400">
                          {user.stats.total_workouts || 0}
                        </div>
                        <div className="text-[10px] text-stone-500">Workouts</div>
                      </div>
                      <div className="glass border border-stone-800 rounded p-1">
                        <div className="text-sm font-bold text-blood-400">
                          {user.stats.battle_wins || 0}
                        </div>
                        <div className="text-[10px] text-stone-500">Wins</div>
                      </div>
                      <div className="glass border border-stone-800 rounded p-1">
                        <div className="text-sm font-bold text-blood-400">
                          {user.stats.win_rate || 0}%
                        </div>
                        <div className="text-[10px] text-stone-500">Rate</div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => sendFriendRequest(user.id, user.username || user.email)}
                      className="flex-1 bg-stone-700 hover:bg-stone-600 text-xs"
                      size="sm"
                    >
                      <UserPlus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                    {user.allow_battle_requests !== false && (
                      <Button
                        onClick={() => challengeUser(user.username || user.email)}
                        className="flex-1 bg-blood-700 hover:bg-blood-800 text-xs"
                        size="sm"
                      >
                        <Swords className="w-3 h-3 mr-1" />
                        Battle
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default FindPeople;
